import { Button, SimpleAlert, StandardAlert, TextArea, TextInput } from '@cimplico/components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateGroups } from '../../queries/use-create-group';
import { useEntities } from '../../queries/use-entities';
import { Entity } from '../../types/entity';
import { GroupTemplateCsv } from '../../types/group';
import { validateData } from './utils';
import plural from '../../utilities/plural';
import Papa from 'papaparse';
import EntitySelect from '../../components/entity-select/EntitySelect';
import CsvFileRowError from '../../components/error-alerts/CsvFileRowError';
import TemplateUploadDropzone from '../../components/file-upload/TemplateUploadDropzone';
import TemplateUploadPreview from '../../components/file-upload/TemplateUploadPreview';
import VerticalRadioInput from '../../components/radio-input/VerticalRadioInput';
import { SubscriptionTier } from '../../types/firm';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

function NewGroupForm(): JSX.Element {
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState<string | undefined>();
    const [description, setDescription] = useState('');
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
    const [isBulk, setIsBulk] = useState(false);
    const [file, setFile] = useState<string | undefined>();
    const [data, setData] = useState<GroupTemplateCsv[] | undefined>();
    const [fileError, setFileError] = useState<string | undefined>(undefined);
    const [errorMessages, setErrorMessages] = useState<{ [rowNumber: string]: string[] }>({});
    const [createGroupError, setCreateGroupError] = useState<{ [name: string]: string[] }>({});

    const [promptUpgrade, setPromptUpgrade] = useState(false);

    const createGroupsQuery = useCreateGroups();
    const useEntitiesQuery = useEntities({ filter: 'hasGroup=false', page: 1, search: '', sort: '' });
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const featureAccess = { canBulkImportGroups: useHasFeatureAccess('group-bulk-import:write') };

    function upload(files: File[]): void {
        setFileError(undefined);
        setFile(files[0].name);

        if (files[0].type !== 'text/csv') {
            setFileError('Invalid file type. File must be .csv extension.');
            return;
        }

        Papa.parse(files[0], {
            header: true,
            complete: function (results) {
                const fieldNames = results.meta.fields as string[];

                const entityColumns = fieldNames.slice(2);
                const invalidColumnExists = entityColumns.find((col) => !/^Entity(_[0-9]+)*$/.test(col)) !== undefined;

                if (fieldNames.length < 3 || fieldNames[0] !== 'Name *' || fieldNames[1] !== 'Description' || invalidColumnExists) {
                    setFileError('Invalid file. The template should be used.');
                    return;
                }

                if (results.data.length === 0) {
                    setFileError('File must contains at least 2 rows of data.');
                    return;
                }
                setData(results.data as GroupTemplateCsv[]);
            },
        });
    }

    function deleteFile(): void {
        setFile(undefined);
        setData(undefined);
        setFileError(undefined);
        setErrorMessages({});
        setCreateGroupError({});
    }

    function handleSourceChange(isBulk: string): void {
        if (isBulk === 'no') {
            deleteFile();
        }

        if (!featureAccess.canBulkImportGroups && isBulk === 'yes') {
            setPromptUpgrade(true);
            setIsBulk(false);
            return;
        }

        setIsBulk(isBulk === 'yes');
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (fileError || Object.keys(errorMessages).length > 0 || Object.keys(createGroupError).length > 0) {
            return;
        }

        if (isBulk) {
            if (!data) {
                setFileError('A file must be uploaded.');
                return;
            }

            if (useEntitiesQuery.data === undefined) {
                return;
            }

            const result = validateData(data, useEntitiesQuery.data.entities);

            if (Object.keys(result[0]).length > 0) {
                setErrorMessages(result[0]);
                return;
            }

            const groupRows = result[1];

            createGroupsQuery.mutate(
                { groups: groupRows },
                {
                    onSuccess: () => {
                        navigate(`/${shortId}/groups`);
                        toastNotification('success', `${groupRows.length} new ${plural(groupRows.length, 'group', 'groups')} created!`);
                    },
                    onError: (res) => {
                        const results = res.response?.data.results;
                        const errorGroups = results.filter((r: { statusCode: number }) => r.statusCode === 400).length;
                        const successGroups = results.filter((r: { statusCode: number }) => r.statusCode === 201).length;

                        const errorResults = results.filter((r: { statusCode: number }) => r.statusCode === 400);

                        const errorMessages: { [name: string]: string[] } = {};
                        for (const res of errorResults) {
                            errorMessages[res.error.original.name] = [res.error.fields.name || res.error.fields.entities];
                        }

                        setCreateGroupError(errorMessages);
                        toastNotification('error', 'Create groups error!', `${successGroups} created. ${errorGroups} failed to create.`);
                    },
                }
            );

            return;
        }

        createGroupsQuery.mutate(
            { groups: [{ name, description, entities: selectedEntities.map((e) => e.id) }] },
            {
                onSuccess: ({ results }) => {
                    navigate(`/${shortId}/groups/${results[0].data?.id}`);
                    toastNotification('success', 'New group created!');
                },
                onError: (err) => {
                    setNameError(err.response?.data.results[0].error.fields.name);
                    toastNotification('error', 'Unable to create new group!');
                },
            }
        );
    }

    function handleCancel(): void {
        navigate(`/${shortId}/groups`);
    }

    return (
        <form className="flex flex-col gap-8 max-w-[450px] mx-auto mt-8" onSubmit={handleSubmit}>
            {promptUpgrade && <UpgradePrompt onClose={(): void => setPromptUpgrade(false)} targetTier={SubscriptionTier.ESSENTIAL} />}
            {fileError && (
                <div className="mb-4">
                    <SimpleAlert type="error" title={fileError} />
                </div>
            )}
            {/* PRINT ERROR MESSAGES FOR EVERY ROWS */}
            {Object.keys(errorMessages).length > 0 && <CsvFileRowError errorMessages={errorMessages} />}

            {/* PRINT ERROR MESSAGES FOR CREATE ERROR */}
            {Object.keys(createGroupError).length > 0 && (
                <CsvFileRowError
                    errorMessages={createGroupError}
                    title={`Failed to create ${Object.keys(createGroupError).length} ${plural(
                        Object.keys(createGroupError).length,
                        'group',
                        'groups'
                    )}`}
                    useRowAsIdentifier={false}
                />
            )}

            {/* INDIVIDUAL OR BULK INPUT */}
            <VerticalRadioInput
                title="Import bulk"
                description="Would you like to bulk import your groups?"
                optionOneLabel="No"
                optionTwoLabel="Yes"
                isDisabledOptionOne={createGroupsQuery.isLoading}
                isDisabledOptionTwo={createGroupsQuery.isLoading}
                value={isBulk ? 'yes' : 'no'}
                onValueChange={handleSourceChange}
            />

            {/* MANUAL CREATE GROUP INPUT */}
            {!isBulk && (
                <>
                    <TextInput
                        placeholder="e.g. The Bloggs Group"
                        title="Group name"
                        defaultValue={name}
                        onChange={(val): void => {
                            setName(val);
                            setNameError(undefined);
                        }}
                        errorMessage={nameError}
                        characterLimit={50}
                        autoFocus
                        isDisabled={createGroupsQuery.isLoading}
                    />
                    <TextArea
                        title="Add a description"
                        isOptional
                        defaultValue={description}
                        onChange={setDescription}
                        placeholder="Description..."
                        form="new-group-form"
                        characterLimit={250}
                        isDisabled={createGroupsQuery.isLoading}
                    />
                    <EntitySelect
                        onChange={setSelectedEntities}
                        title="Entities"
                        isOptional
                        isMulti
                        isDisabled={createGroupsQuery.isLoading}
                    />
                </>
            )}

            {isBulk && (
                <StandardAlert
                    type="info"
                    title="Ensure the following"
                    description={[
                        'Group names must be unique.',
                        'Entity names must match an existing entity.',
                        "Add ONE entity name per 'Entity' column.",
                        "To add more entities, create columns with the heading 'Entity'.",
                    ]}
                >
                    <div></div>
                </StandardAlert>
            )}
            {/* FILE UPLOAD SECTION */}
            {isBulk && !file && (
                <TemplateUploadDropzone
                    columns={['"Name *","Description","Entity","Entity","Entity"']}
                    fileName="group-template.csv"
                    onUpload={upload}
                />
            )}

            {/* SHOW UPLOADED FILE NAME */}
            {file && <TemplateUploadPreview fileName={file} onDelete={deleteFile} />}

            {/* FORM CANCEL + SUBMIT */}
            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={handleCancel} disabled={createGroupsQuery.isLoading} />
                <Button
                    type="primary"
                    label={createGroupsQuery.isLoading ? 'Creating...' : 'Create Group'}
                    disabled={createGroupsQuery.isLoading}
                    isLoading={createGroupsQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}
export default NewGroupForm;
